import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgbPopover, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { isEqual } from "lodash";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableLabelDirective } from "rl-common/components/form-table/form-table-label.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { ISigmaControlMapping, ISigmaSectionParams, SigmaControlSource } from "rl-common/components/mod-details-layout/mod-layout-sections-params.models";
import { IPanelSection } from "rl-common/components/mod-details-layout/mod-layout.models";
import { SelectSigmaObjectComponent, SigmaObjectLevel } from "rl-common/components/select-sigma-object/select-sigma-object.component";
import { ISelectedSigmaObject } from "rl-common/components/select-sigma-object/select-sigma-object.models";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { ICharacteristicMetaDataCollection } from "rl-common/models/i-characteristic-meta-data-collection";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { CharDataType } from "rl-common/rl-common.consts";
import { GrowlerService } from "rl-common/services/growler.service";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { catchError, distinctUntilChanged, switchMap, takeUntil, tap } from "rxjs/operators";
import { ISigmaControl } from "../../../../../+sigma/services/sigma.models";
import { SigmaService } from "../../../../../+sigma/services/sigma.service";

/** One selectable source of a control value. `key` is what the dropdown round-trips. */
interface IControlSourceOption {
	key: string;
	label: string;
	source: SigmaControlSource;
	characteristicId?: number;
}

const RECORD_ID_OPTION_KEY = "recordId";

@Component({
	selector: "rl-build-sigma-element",
	templateUrl: "./build-sigma-element.component.html",
	styleUrls: ["./build-sigma-element.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormTableRowComponent, FormTableLabelDirective, FormTableControlDirective, TextInputComponent, DropdownSingleComponent, SelectSigmaObjectComponent, ReactiveFormsModule, FormsModule, NgbPopover, NgbTooltip]
})
export class BuildSigmaElementComponent implements OnInit, OnChanges, OnDestroy {
	private readonly _sigmaService = inject(SigmaService);
	private readonly _growler = inject(GrowlerService);


	@Input()
	section: IPanelSection<ISigmaSectionParams>;

	@Input()
	templateMetaData: ICharacteristicMetaDataCollection;

	@Output()
	paramsChange = new EventEmitter<ISigmaSectionParams>();

	/**
	 * True when this section is the whole panel (PanelGroupType.SigmaReport). A dedicated panel can
	 * hold a whole workbook or page, whereas a section sitting alongside other sections is limited
	 * to a single element.
	 */
	@Input()
	isDedicatedPanel = false;

	/** Rebuilt from `isDedicatedPanel` in ngOnChanges rather than computed, so the dropdown keeps a stable options object. */
	selectableLevels: SigmaObjectLevel[] = this.buildSelectableLevels();

	title: string;
	selected: ISelectedSigmaObject;

	/**
	 * Controls on the selected workbook, as the dropdown's options. Holds only the saved control until
	 * the workbook's own controls arrive, so a reopened section shows its mapping without waiting on
	 * Sigma. Empty when no workbook is selected, or when the workbook has no controls.
	 */
	controlOptions: DropdownOptions<ISigmaControl> = this.buildControlOptions([]);
	isLoadingControls = false;

	sourceOptions: DropdownOptions<IControlSourceOption> = this.buildSourceOptions();

	/** The control that gets preselected. Null leaves the report unfiltered. */
	selectedControl: ISigmaControl;

	/** The value that control is preselected to. */
	selectedSource: IControlSourceOption;

	/** Shows Sigma's element toolbar on the record. Off unless the designer opts in. */
	isInteractive = false;

	private readonly _workbookId$ = new BehaviorSubject<string>(null);
	private readonly _destroyed$ = new Subject<void>();

	/** Controls Sigma reported for the selected workbook. Null while they are unknown. */
	private _loadedControls: ISigmaControl[];

	get selectedObjects() {
		return this.selected ? [this.selected] : [];
	}

	/** Nothing to pick from until a workbook is selected and its controls are known. */
	get isControlSelectDisabled() {
		return this.isLoadingControls || this.controlOptions.items.length === 0;
	}

	ngOnInit(): void {
		this._workbookId$
			.pipe(
				distinctUntilChanged(),
				tap(workbookId => {
					// the control list belongs to the workbook it came from
					this._loadedControls = null;
					this.isLoadingControls = !!workbookId;
				}),
				switchMap(workbookId => this.loadControls(workbookId)),
				takeUntil(this._destroyed$)
			)
			.subscribe(controls => {
				this.isLoadingControls = false;
				// a failed load is not a reason to drop what the designer saved, so leave it showing
				if (!controls) {
					return;
				}
				this._loadedControls = controls;
				this.applyControls(this.selectedControl?.name);
				this.emitIfMappingResolvedAway();
			});
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.isDedicatedPanel) {
			this.selectableLevels = this.buildSelectableLevels();
		}
		if (changes.templateMetaData) {
			this.sourceOptions = this.buildSourceOptions();
		}
		if (changes.section) {
			const params = this.section?.params;
			this.title = params?.title;
			this.isInteractive = !!params?.isInteractive;
			this.selected = params?.workbookId ? {
				workbookId: params.workbookId,
				workbookUrl: params.workbookUrl,
				pageId: params.pageId,
				elementId: params.elementId,
				name: params.objectName ?? params.title
			} : null;
			// before the mapping is restored, so a changed workbook discards the stale control list first
			this._workbookId$.next(this.selected?.workbookId ?? null);
		}
		if (changes.section || changes.templateMetaData) {
			const mapping = this.section?.params?.controlMapping;
			this.applyControls(mapping?.controlName);
			this.selectedSource = this.findSourceOption(mapping);
			this.emitIfMappingResolvedAway();
		}
	}

	ngOnDestroy(): void {
		this._destroyed$.next();
		this._destroyed$.complete();
	}

	objectSelected(selected: ISelectedSigmaObject) {
		// the title names the report, so it follows the selection rather than describing whichever
		// object happened to be picked first. A builder who wants their own wording types it after
		// choosing the report; changing the report again re-defaults it.
		this.title = selected.name;
		// a control belongs to a specific workbook, so the mapping cannot survive a different one
		if (selected.workbookId !== this.selected?.workbookId) {
			this._loadedControls = null;
			this.applyControls(null);
			this.selectedSource = null;
		}
		this.selected = selected;
		this._workbookId$.next(selected.workbookId);
		this.updateParams();
	}

	/**
	 * The record's own id is what a context filter usually matches on, so choosing a filter offers it
	 * ready to save rather than making the designer pick it every time.
	 */
	controlChanged() {
		if (!this.selectedControl) {
			this.selectedSource = null;
		} else if (!this.selectedSource) {
			this.selectedSource = this.findSourceOption({ controlName: null, source: SigmaControlSource.RecordId });
		}
		this.updateParams();
	}

	updateParams() {
		if (!this.selected) {
			return;
		}
		const params: ISigmaSectionParams = {
			workbookId: this.selected.workbookId,
			workbookUrl: this.selected.workbookUrl,
			pageId: this.selected.pageId,
			elementId: this.selected.elementId,
			title: this.title,
			objectName: this.selected.name,
			controlMapping: this.buildControlMapping(),
			isInteractive: this.isInteractive
		};
		this.paramsChange.emit(params);
	}

	/** Emits null when the workbook's controls could not be fetched, leaving them unknown. */
	private loadControls(workbookId: string): Observable<ISigmaControl[]> {
		if (!workbookId) {
			return of([]);
		}
		return this._sigmaService.getWorkbookControls(workbookId)
			.pipe(catchError(() => {
				this._growler.error().growl("Unable to load the report's context filters.");
				return of(null as ISigmaControl[]);
			}));
	}

	/**
	 * A dedicated panel can hold a whole workbook or page. A section sitting alongside other sections
	 * only gets the space for a single element.
	 */
	private buildSelectableLevels(): SigmaObjectLevel[] {
		return this.isDedicatedPanel
			? [SigmaObjectLevel.Workbook, SigmaObjectLevel.Page]
			: [SigmaObjectLevel.Element];
	}

	/**
	 * Sigma reports no display label for a control, so the author's Control ID is what the designer picks
	 * from -- see the controls endpoint on ReportTeamProvider.
	 */
	private buildControlOptions(controls: ISigmaControl[]): DropdownOptions<ISigmaControl> {
		return {
			items: controls,
			rowKeyFn: (control: ISigmaControl) => control.name,
			rowLabelFn: (control: ISigmaControl) => control.name
		};
	}

	/**
	 * Only identifier fields are offered. A context filter matches on a key that identifies the record
	 * in the report's data, which is what an identifier field holds; a free text or date field would
	 * filter to nothing.
	 */
	private buildSourceOptions(): DropdownOptions<IControlSourceOption> {
		const items: IControlSourceOption[] = [
			{ key: RECORD_ID_OPTION_KEY, label: "Record ID", source: SigmaControlSource.RecordId }
		];
		const identifierFields = (this.templateMetaData?.characteristicMetaDatas ?? [])
			.filter(cmd => cmd.dataTypeID === CharDataType.Identifier);
		for (const field of identifierFields) {
			items.push({
				key: this.fieldOptionKey(field.characteristicID),
				label: field.label,
				source: SigmaControlSource.Field,
				characteristicId: field.characteristicID
			});
		}
		return {
			items,
			rowKeyFn: (option: IControlSourceOption) => option.key,
			rowLabelFn: (option: IControlSourceOption) => option.label
		};
	}

	private fieldOptionKey(characteristicId: number) {
		return `field:${characteristicId}`;
	}

	/**
	 * Points the control dropdown at `mappedControlName`, offering whatever is known to pick from. Until
	 * Sigma answers, that is the mapped control on its own: a control's name is also its label, so the
	 * saved mapping can be shown immediately instead of leaving the designer looking at an empty dropdown
	 * over saved settings. Once the workbook's controls are known, a name it no longer has resolves to
	 * nothing, which clears the selection and drops the mapping on the next save.
	 */
	private applyControls(mappedControlName: string) {
		const items = this._loadedControls
			?? (mappedControlName ? [{ name: mappedControlName }] : []);
		this.controlOptions = this.buildControlOptions(items);
		this.selectedControl = items.find(control => control.name === mappedControlName);
	}

	/**
	 * A mapped field that is no longer on the template resolves to nothing, which leaves the value unset
	 * and drops the mapping on save.
	 */
	private findSourceOption(mapping: ISigmaControlMapping): IControlSourceOption {
		if (!mapping) {
			return null;
		}
		const key = mapping.source === SigmaControlSource.Field
			? this.fieldOptionKey(mapping.characteristicId)
			: RECORD_ID_OPTION_KEY;
		return this.sourceOptions.items.find(x => x.key === key);
	}

	/** A filter with no value, or a value with no filter to send it to, is not a mapping. */
	private buildControlMapping(): ISigmaControlMapping {
		if (!this.selectedControl || !this.selectedSource) {
			return undefined;
		}
		const mapping: ISigmaControlMapping = {
			controlName: this.selectedControl.name,
			source: this.selectedSource.source
		};
		// only the record's own fields are addressed by id, and the key is left off entirely otherwise so a
		// rebuilt mapping compares equal to the saved one (see emitIfMappingResolvedAway)
		if (this.selectedSource.source === SigmaControlSource.Field) {
			mapping.characteristicId = this.selectedSource.characteristicId;
		}
		return mapping;
	}

	/**
	 * Saves the section when resolving its mapping changed it -- a control the workbook no longer has, or a
	 * field no longer on the template, resolves to nothing and clears the selection. `section.params` is only
	 * ever written by our own `paramsChange`, so without this the designer would see the filter correctly
	 * blank while the layout kept saving a mapping to something that no longer exists.
	 *
	 * Emitting only on a real difference matters: `updateParams` normalizes as it emits (it always writes
	 * `height` and `objectName`), so an unconditional emit would leave a section saved before those existed
	 * looking edited to LayoutBuilderComponent.pristine the moment its panel was expanded.
	 */
	private emitIfMappingResolvedAway() {
		if (isEqual(this.section?.params?.controlMapping, this.buildControlMapping())) {
			return;
		}
		this.updateParams();
	}

}
